import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import useGetMoveTokenInfo from '@/hooks/api/useGetMoveTokenInfo';
import useSetLoading from '@/hooks/useSetLoading';
import alertState from '@/states/alertState';
import { MoveTokenResponse } from '@/types/api/mypage/moveTokenResponse';
import { isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';

import ExternalLink from '../atoms/ExternalLink';
import Heading from '../atoms/Heading';
import LinkButton from '../atoms/LinkButton';

/**
 * 移転トークン情報を取得するカスタムフック
 * マウント時に移転トークンAPIを呼び出し、発行済みトークンがあれば返す。
 * トークンの有無で画面上の「発行」「確認/再発行」ボタンの出し分けに使う。
 *
 * @returns moveToken - 移転トークン情報（未発行時はundefined）
 */
export const useMoveTokenInfo = () => {
  const [moveToken, setMoveToken] = useState<MoveTokenResponse>();
  const { startLoading, finishLoading } = useSetLoading();
  const setAlert = useSetRecoilState(alertState);
  const { execute: getMoveTokenInfo } = useGetMoveTokenInfo();

  // 移転トークン情報取得API呼び出し
  const callAPIGetMoveTokenInfo = async () => {
    startLoading(ApiUrl.Read.MOVE_TOKEN_INFO);
    await getMoveTokenInfo()
      .then(async ({ data }) => {
        // 取得した値をセット
        setMoveToken(data);
      })
      .catch(async (error) => {
        if (isExpiredToken(error)) {
          window.location.href = url('/login');
          sessionStorage.clear();
          return;
        }
        setAlert({
          isOpen: true,
          apiType: Constants.ApiType.API_GET,
        });
      })
      .finally(async () => {
        finishLoading(ApiUrl.Read.MOVE_TOKEN_INFO);
      });
  };

  useEffect((): void => {
    callAPIGetMoveTokenInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { moveToken };
};

/**
 * 引越し手続き案内コンポーネント
 * 移転トークンの発行状況に応じて「コード発行」または「コード確認/再発行」ボタンを切り替え表示する。
 * 引越し特典の詳細や手続きフローへの外部リンクも含む。
 */
const PlanInfoMoving = () => {
  const { moveToken } = useMoveTokenInfo();

  return (
    <>
      <Heading as='h3'>移転(お引越し)の手続き</Heading>
      <p className='m-text' data-testid='moving-text'>
        現在NURO 光をご利用中で、お引越し先でもNURO 光をご利用予定のお客様は、現住所のNURO
        光解約費用をキャッシュバックでお受け取りいただけます。
        <br />
        ※NURO 光の新規お申し込みにあたり、お引越し元のNURO 光解約で発生する費用に限ります。
        <br />
        <br />
        更にお引越し先のお住まいでは、下記の特典が受けられます。
        <br />
        ・初月の月額基本料金無料
        <br />
        ・契約事務手数料3,300円相当割引（2ヶ月目の月額料金から割引）
        <br />
        ・工事費実質無料
        <br />
        ・設定サポート1回無料
        <br />
        <br />
        上記の特典をご利用される場合は、「お引越しコードを発行する」ボタンをクリックしてお手続きを開始してください。既にご発行済みのお引越しコードをご確認される方は、「お引越しコードを確認する」をクリックしてください。
        <br />
        <br />
        お引越し手続きの流れや特典の詳細については下記ページをご確認ください。
      </p>
      ・お引越し先でもNURO特典の詳細は
      <ExternalLink href='https://www.nuro.jp/hikari/campaign/hikkoshi/'>
        お引越し先でもNURO 光特典について詳しく
      </ExternalLink>
      <br />
      ・お引越し手続きの流れについては
      <ExternalLink href='https://www.nuro.jp/hikari/campaign/hikkoshi/#flow'>
        お引越し手続きの流れ
      </ExternalLink>
      {moveToken?.move_token && (
        <div className='m-button col2'>
          <LinkButton variant='secondary' href='/contract/move_token'>
            お引越しコードを確認する
          </LinkButton>
          <LinkButton variant='secondary' href='/contract/move_token/create'>
            お引越しコードを再発行する
          </LinkButton>
        </div>
      )}
      {!moveToken?.move_token && (
        <div className='m-button col2'>
          <LinkButton variant='secondary' href='/contract/move_token/create'>
            お引越しコードを発行する
          </LinkButton>
        </div>
      )}
    </>
  );
};

export default PlanInfoMoving;
